import { Transform, Type } from 'class-transformer';
import {
	IsArray,
	IsBoolean,
	IsEnum,
	IsIn,
	IsInt,
	IsISO8601,
	IsNotEmpty,
	IsNumber,
	IsObject,
	IsOptional,
	IsString,
	IsUrl,
	IsUUID,
	Max,
	MaxLength,
	Min,
	Validate,
	ValidateNested,
	ValidationArguments,
	ValidatorConstraint,
	ValidatorConstraintInterface,
} from 'class-validator';
import { MarketStatus } from '../../enums/market-status.enum';
import { LeaderboardParticipantType } from '../../enums/leaderboard-participant-type.enum';
import { LeaderboardScoringMode } from '../../enums/leaderboard-scoring-mode.enum';
import { ProbabilityShareType } from '../../enums/probability-share-type.enum';
import { ensureHttpsUrl } from '../../utils/ensure-https-url.util';
import { MATCH_QUESTION_RESULT_FILTERS } from '../../utils/match-question-filters.utils';
import { MatchDto } from './match.dto';
import { PostEventCarouselDto } from './probability-event-post-final-match-state.dto';
import { LeaderboardFilterMode, UserGroup } from '../../enums';

type MatchQuestionResultFilter = (typeof MATCH_QUESTION_RESULT_FILTERS)[number];

type MatchQuestionCategory = {
	id: string;
	name: string;
};

type MatchQuestionResultCounts = {
	beat: number;
	below: number;
	pending: number;
};

export const MAX_CUSTOM_RANGE_DAYS = 90;

export class PredictionProbabilityDto {
	@IsNumber()
	@Min(0.01)
	@Max(0.99)
	probability: number;
}

export class CreatePredictionDto extends PredictionProbabilityDto {
	@IsUUID()
	@IsNotEmpty()
	marketId: string;

	@IsUUID()
	@IsNotEmpty()
	lobbyId: string;
}

export class PredictionDto {
	@IsUUID()
	id: string;

	@IsUUID()
	market_id: string;

	@IsNumber()
	probability: number;

	@IsNumber()
	@IsOptional()
	brier_score?: number | null;

	@IsString()
	question: string;
}

export class ProbabilityLeaderboardParamsDto {
	@IsInt()
	@Min(0)
	@IsOptional()
	@Type(() => Number)
	skip?: number;

	@IsInt()
	@Min(1)
	@Max(100)
	@IsOptional()
	@Type(() => Number)
	limit?: number;

	@IsIn(['asc', 'desc'])
	@IsOptional()
	sortDirection?: 'asc' | 'desc';

	@IsIn(Object.values(LeaderboardParticipantType))
	@IsOptional()
	type?: LeaderboardParticipantType;

	@IsString()
	@IsOptional()
	userId?: string;

	@IsString()
	@IsOptional()
	college?: string;

	@IsString()
	@IsOptional()
	country?: string;

	@IsEnum(UserGroup)
	@IsOptional()
	group?: UserGroup;

	@IsArray()
	@IsString({ each: true })
	@IsOptional()
	friendUserIds?: string[];
}

export class GetProbabilityLeaderboardDto extends ProbabilityLeaderboardParamsDto {
	@IsIn(['global', 'match'])
	leaderboardType: 'global' | 'match';

	@IsUUID()
	@IsNotEmpty()
	eventId: string;

	@IsISO8601()
	@IsOptional()
	startDate?: string;

	@IsISO8601()
	@IsOptional()
	endDate?: string;

	@IsUUID()
	@IsOptional()
	slateId?: string;

	@IsArray()
	@IsUUID('4', { each: true })
	@IsNotEmpty()
	@IsOptional()
	matchIds?: string[];
}

export class RefreshProbabilityLeaderboardDto {
	@IsString()
	@IsNotEmpty()
	eventId: string;
}

export class ProbabilityLeaderboardItemDto {
	user_id: string;
	score: number | null;
	full_name: string;
	img_variants: string | null;
	country?: string | null;
	label?: string | null;
	is_me?: boolean;
	rank?: number;
	status?: 'completed' | 'in_progress' | null;
}

export class PaginatedLeaderboardResponseDto {
	items: ProbabilityLeaderboardItemDto[];
	total: number;
	me?: ProbabilityLeaderboardItemDto;
	is_today?: boolean;
}

export class ProbabilityLeaderboardData {
	user_id: string;
	rbs: number;
}

export class LeaderboardRecord {
	user_id: string;
	score: number | null;
}

export class GetPredictionsDto {
	@IsUUID()
	@IsNotEmpty()
	matchId: string;

	@IsUUID()
	@IsNotEmpty()
	lobbyId: string;
}

export class GetUserRankDto {
	@IsUUID()
	@IsNotEmpty()
	userId: string;

	@IsString()
	@IsNotEmpty()
	eventId: string;

	@IsString()
	@IsNotEmpty()
	lobbyId: string;

	@IsBoolean()
	@IsOptional()
	includeStreak?: boolean;

	@IsIn(['global'])
	@IsOptional()
	leaderboardType?: 'global';

	@IsEnum(LeaderboardScoringMode)
	@IsOptional()
	scoringMode?: LeaderboardScoringMode;

	@IsOptional()
	@IsString()
	matchId?: string;

	@IsISO8601()
	@IsOptional()
	startDate?: string;

	@IsISO8601()
	@IsOptional()
	endDate?: string;

	@IsUUID()
	@IsOptional()
	slateId?: string;
}

export class UserRankDto {
	score: number;
	smart_rating: number;
	rank: number;
	total: number;
	fullName?: string;
	imgVariants?: object | null;
	label?: string | null;
	canBeFollowed?: boolean;
	streak?: number;
	country?: string;
	score_change: number | null;
	rank_change: number | null;
}

export class GetUserLobbyActivityDto {
	@IsUUID()
	@IsNotEmpty()
	lobbyId: string;

	@IsUUID()
	@IsOptional()
	targetUserId?: string;
}

export class GetUserLobbyActivityMatchDto extends GetUserLobbyActivityDto {
	@IsUUID()
	@IsNotEmpty()
	matchId: string;
}

export class UserLobbyActivityMarketDto {
	marketId: string;
	question: string;
	current_value: number;
	status: MarketStatus;
	prediction: {
		id: string;
		probability: number;
		brier_score: number | null;
		relative_brier_score: number | null;
	};
}

export class UserLobbyActivityMatchesGroupedDto {
	ongoing: UserLobbyActivityMatchDto[];
	settled: UserLobbyActivityMatchDto[];
}

export class UserLobbyActivityMatchDto {
	matchId: string;
	name: string;
	closing_time: Date;
	markets: UserLobbyActivityMarketDto[];
	totalMarkets: number;
	opening_time: Date;
	relative_brier_score: number | null;
	home_competitors: any;
	away_competitors: any;
	all_markets_settled: boolean;
	result?: MatchResult | null;
}

export class GetMatchPredictionStatsDto {
	@IsUUID()
	@IsNotEmpty()
	matchId: string;

	@IsUUID()
	@IsNotEmpty()
	lobbyId: string;

	@IsUUID()
	@IsOptional()
	targetUserId?: string;

	@IsString()
	@IsOptional()
	@MaxLength(200)
	search?: string;

	/**
	 * Question chip id: admin Generic Market Group UUID, or derived
	 * PerformanceCategory key (sport-specific vocabulary — soccer or tennis)
	 * when category_id is missing (OpticOdds).
	 */
	@IsString()
	@IsOptional()
	@MaxLength(100)
	categoryId?: string;

	@IsIn([...MATCH_QUESTION_RESULT_FILTERS])
	@IsOptional()
	resultFilter?: MatchQuestionResultFilter;
}

export class MatchPredictionStatItemDto {
	id: string;
	question: string;
	// null while the market is open — the odds reference price is gated pre-forecast (JWB-4717).
	current_value: number | null;
	prediction: number | null;
	prediction_average: number | null;
	relative_brier_score: number | null;
	potential_rbp_if_happening?: number | null;
	potential_rbp_if_not_happening?: number | null;
	status: MarketStatus;
	category_id?: string | null;
	category_name?: string | null;
	question_number: number;
}

export class MatchPredictionStatsDto {
	relative_brier_score: number | null;
	brier_average: number | null;
	brier_user: number | null;
	all_markets_settled: boolean;
	total_potential_rbp_if_happening: number | null;
	total_potential_rbp_if_not_happening: number | null;
	markets: MatchPredictionStatItemDto[];
	markets_total: number;
	categories: MatchQuestionCategory[];
	result_counts: MatchQuestionResultCounts;
}

export class GetMatchCrowdStatsDto {
	@IsUUID()
	@IsNotEmpty()
	matchId: string;

	@IsUUID()
	@IsNotEmpty()
	lobbyId: string;

	@IsString()
	@IsOptional()
	@MaxLength(200)
	search?: string;

	@IsString()
	@IsOptional()
	@MaxLength(100)
	categoryId?: string;
}

export class MatchCrowdStatItemDto {
	id: string;
	question: string;
	// null while the market is open — the odds reference price is gated pre-forecast (JWB-4717).
	current_value: number | null;
	prediction_average: number | null;
	status: MarketStatus;
	category_id?: string | null;
	category_name?: string | null;
	/** 1-based order in the unfiltered match market list (stable under filters). */
	question_number: number;
}

export class MatchCrowdStatsDto {
	markets: MatchCrowdStatItemDto[];
	markets_total: number;
	categories: MatchQuestionCategory[];
}

export class GetLobbyMatchesDto {
	@IsUUID()
	@IsNotEmpty()
	lobbyId: string;

	@IsUUID()
	@IsOptional()
	userId?: string;
}
export class MatchSetScore {
	home: number;
	away: number;
	isTiebreak?: boolean;
}

export class MatchResult {
	homeScore?: number | null;
	awayScore?: number | null;
	period?: string | null;
	clock?: string | null;
	winner?: 'home' | 'away' | null;
	periods?: MatchSetScore[];
	gameScore?: { home: string; away: string } | null;
	isLive?: boolean;
	fixtureStatus?: string | null;
	startDate?: string | null;
	retirementInfo?: {
		winner: string;
		winnerTeamId: string;
		walkover: boolean;
	} | null;
	hasScore?: boolean | null;
}

export class SlateDto {
	id: string;

	created_date: Date;
	updated_date: Date;
	deleted_date?: Date;

	created_by: string;

	event_id: string;

	name: string;
	description: string;

	order: number;

	opening_date: Date | null;
	closing_date: Date | null;

	generated_by?: string | null;
	group_name?: string | null;
}

export class MatchDtoWithSlate extends MatchDto {
	slate?: SlateDto;
}

export class LobbyMatchItemDto extends MatchDto {
	markets_count: number;
	prediction_count: number;

	slate?: SlateDto;
}

export class UpdateMarketCurrentValueDto {
	@IsUUID()
	@IsNotEmpty()
	marketId: string;

	@IsInt()
	@Min(1)
	@Max(99)
	currentValue: number;
}

export class CheckMatchPredictionsDto {
	@IsUUID()
	@IsNotEmpty()
	matchId: string;

	@IsUUID()
	@IsNotEmpty()
	lobbyId: string;
}

export class HasPredictionsDto {
	hasPredictions: boolean;
}

export class LatestProbabilityEventDto {
	id: string;
	title: string;
	description: string | null;
	image_url: string | null;
	img_variants: object | null;
	hasJoined: boolean;
}

export class NearestFeaturedMatchDto extends MatchDto {
	markets_count: number;
	prediction_count: number;
}

export class GetUserPredictedMatchesDto {
	@IsUUID()
	@IsNotEmpty()
	lobbyId: string;

	@IsNumber()
	@IsOptional()
	limit?: number;

	@IsBoolean()
	@IsOptional()
	includeSettled?: boolean;

	@IsUUID()
	@IsOptional()
	apiKeyId?: string;

	@IsIn(['user', 'bots'])
	@IsOptional()
	participantType?: 'user' | 'bots';
}

export class UserPredictedMatchDto extends MatchDto {
	prediction_count: number;
	markets_count: number;
	relative_brier_score: number | null;
}

export class UserPredictedMatchesGroupedDto {
	live: UserPredictedMatchDto[];
	preMatch: UserPredictedMatchDto[];
}

export class GetUserParticipatedMatchesDto {
	@IsUUID()
	@IsNotEmpty()
	lobbyId: string;
}

export class UserParticipatedMatchItemDto {
	matchId: string;
	relative_brier_score: number | null;
	all_markets_settled: boolean;
}

export class GetProbabilityAchievementsDto {
	@IsUUID()
	@IsNotEmpty()
	userId: string;

	@IsUUID()
	@IsOptional()
	eventId?: string;
}

/** Request body for endpoints that optionally scope the result to one probability event. */
export class OptionalEventScopeDto {
	@IsUUID()
	@IsOptional()
	eventId?: string;
}

export class GetMyBotsDto {
	@IsString()
	@IsNotEmpty()
	eventId: string;

	@IsString()
	@IsNotEmpty()
	lobbyId: string;

	@IsOptional()
	@IsArray()
	@IsString({ each: true })
	matchIds?: string[];

	@IsIn(['global', 'match'])
	@IsOptional()
	leaderboardType?: 'global' | 'match';

	@IsISO8601()
	@IsOptional()
	startDate?: string;

	@IsISO8601()
	@IsOptional()
	endDate?: string;

	@IsUUID()
	@IsOptional()
	slateId?: string;

	@IsIn(Object.values(LeaderboardParticipantType))
	@IsOptional()
	type?: LeaderboardParticipantType;
}

export class MyBotDto {
	id: string;
	label: string | null;
	last_used_at: string | null;
	score: number | null;
	rank: number | null;
	total?: number;
}

export class BotWithPredictionsDto {
	id: string;
	label: string | null;
	user_full_name: string | null;
	total: number;
	event_id: string | null;
	event_name: string | null;
}

export interface MatchWinnerItemDto {
	userId: string;
	userFullName: string | null;
	matchId: string;
	matchName: string;
	homeName: string | null;
	homeLogo: string | null;
	awayName: string | null;
	awayLogo: string | null;
	homeScore: number | null;
	awayScore: number | null;
}

export class ProbabilityResourceGuideItemDto {
	@IsUUID()
	@IsNotEmpty()
	id: string;

	@IsString()
	@IsNotEmpty()
	@MaxLength(120)
	title: string;

	@IsString()
	@IsNotEmpty()
	@MaxLength(200)
	description: string;

	@Transform(({ value }) => (typeof value === 'string' ? ensureHttpsUrl(value) : value))
	@IsString()
	@IsNotEmpty()
	@IsUrl({ protocols: ['http', 'https'], require_protocol: true })
	@MaxLength(2000)
	href: string;

	@IsUUID()
	@IsOptional()
	iconImgParentId?: string | null;

	@IsOptional()
	iconImgVariants?: any | null;
}

export class ProbabilityEventDetailsDto {
	id: string;
	eventId: string;
	supportLink: string | null;
	mobileBannerImgParentId: string | null;
	mobileBannerImgVariants: any | null;
	desktopBannerImgParentId: string | null;
	desktopBannerImgVariants: any | null;
	partnerLogoImgParentId: string | null;
	partnerLogoImgVariants: any | null;
	scoringLink: string | null;
	scoringText: string | null;
	scoringLeaderboardLink: string | null;
	scoringLeaderboardText: string | null;
	shareTexts: any | null;
	achievementSetKey: string | null;
	scoreBoostTitle: string | null;
	scoreBoostBodyCopy: string | null;
	scoreBoostTooltipCopy: string | null;
	scoreBoostMobileImgParentId: string | null;
	scoreBoostMobileImgVariants: any | null;
	scoreBoostDesktopImgParentId: string | null;
	scoreBoostDesktopImgVariants: any | null;
	slateLeaderboardGuideTitle: string | null;
	slateLeaderboardGuideBodyCopy: string | null;
	slateLeaderboardGuideMobileImgParentId: string | null;
	slateLeaderboardGuideMobileImgVariants: any | null;
	slateLeaderboardGuideDesktopImgParentId: string | null;
	slateLeaderboardGuideDesktopImgVariants: any | null;
	newsfeedTitle: string | null;
	resourcesGuideTitle: string | null;
	resourcesGuideIntro: string | null;
	resourcesGuideItems: ProbabilityResourceGuideItemDto[] | null;
	resourcesCoachmarkTitle: string | null;
	resourcesCoachmarkBody: string | null;
	eventWrappedText: string | null;
	postFinalMatchCarousel: PostEventCarouselDto | null;
	leaderboardFilterMode: LeaderboardFilterMode | null;
	autoActiveSlate: boolean;
	activeSlateId: string | null;
}

export class UpsertProbabilityEventDetailsDto {
	@IsUUID()
	@IsNotEmpty()
	eventId: string;

	@IsString()
	@IsOptional()
	supportLink?: string;

	@IsUUID()
	@IsOptional()
	partnerLogoImgParentId?: string | null;

	@IsOptional()
	@IsObject()
	partnerLogoImgVariants?: any | null;

	@IsUUID()
	@IsOptional()
	mobileBannerImgParentId?: string | null;

	@IsOptional()
	mobileBannerImgVariants?: any | null;

	@IsUUID()
	@IsOptional()
	desktopBannerImgParentId?: string | null;

	@IsOptional()
	desktopBannerImgVariants?: any | null;

	@IsString()
	@IsOptional()
	scoringLink?: string;

	@IsString()
	@IsOptional()
	scoringText?: string;

	@IsString()
	@IsOptional()
	scoringLeaderboardLink?: string;

	@IsString()
	@IsOptional()
	scoringLeaderboardText?: string;

	@IsOptional()
	@IsObject()
	shareTexts?: Record<string, unknown>;

	@IsString()
	@IsOptional()
	achievementSetKey?: string;

	@IsString()
	@IsOptional()
	@MaxLength(120)
	resourcesGuideTitle?: string;

	@IsString()
	@IsOptional()
	@MaxLength(400)
	resourcesGuideIntro?: string;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => ProbabilityResourceGuideItemDto)
	resourcesGuideItems?: ProbabilityResourceGuideItemDto[] | null;

	@IsString()
	@IsOptional()
	@MaxLength(120)
	resourcesCoachmarkTitle?: string;

	@IsString()
	@IsOptional()
	@MaxLength(240)
	resourcesCoachmarkBody?: string;

	@IsOptional()
	@ValidateNested()
	@Type(() => PostEventCarouselDto)
	postFinalMatchCarousel?: PostEventCarouselDto | null;
	@IsEnum(LeaderboardFilterMode)
	@IsOptional()
	leaderboardFilterMode?: LeaderboardFilterMode;

	@IsBoolean()
	@IsOptional()
	autoActiveSlate?: boolean;

	@IsUUID()
	@IsOptional()
	activeSlateId?: string | null;
}

export class GetProbabilityEventDetailsDto {
	@IsUUID()
	@IsNotEmpty()
	eventId: string;
}

const SCORE_BOOST_FIELDS = [
	'scoreBoostTitle',
	'scoreBoostBodyCopy',
	'scoreBoostTooltipCopy',
	'scoreBoostMobileImgParentId',
	'scoreBoostMobileImgVariants',
	'scoreBoostDesktopImgParentId',
	'scoreBoostDesktopImgVariants',
] as const;

@ValidatorConstraint({ name: 'AllScoreBoostFieldsOrNone' })
class AllScoreBoostFieldsOrNoneConstraint implements ValidatorConstraintInterface {
	validate(_value: unknown, args: ValidationArguments): boolean {
		const dto = args.object as Record<string, unknown>;
		const filledCount = SCORE_BOOST_FIELDS.filter((field) => dto[field] != null).length;
		return filledCount === 0 || filledCount === SCORE_BOOST_FIELDS.length;
	}

	defaultMessage(): string {
		return 'Score Boost fields must be either all filled or all null.';
	}
}

/** Fields must be either all filled (upsert) or all null (clears the record) — enforced by AllScoreBoostFieldsOrNoneConstraint. */
export class UpsertProbabilityScoreBoostDto {
	@IsUUID()
	@IsNotEmpty()
	eventId: string;

	@IsString()
	@IsOptional()
	@Validate(AllScoreBoostFieldsOrNoneConstraint)
	scoreBoostTitle?: string | null;

	@IsString()
	@IsOptional()
	scoreBoostBodyCopy?: string | null;

	@IsString()
	@IsOptional()
	scoreBoostTooltipCopy?: string | null;

	@IsUUID()
	@IsOptional()
	scoreBoostMobileImgParentId?: string | null;

	@IsOptional()
	@IsObject()
	scoreBoostMobileImgVariants?: any | null;

	@IsUUID()
	@IsOptional()
	scoreBoostDesktopImgParentId?: string | null;

	@IsOptional()
	@IsObject()
	scoreBoostDesktopImgVariants?: any | null;
}

const SLATE_LEADERBOARD_GUIDE_FIELDS = [
	'slateLeaderboardGuideTitle',
	'slateLeaderboardGuideBodyCopy',
	'slateLeaderboardGuideMobileImgParentId',
	'slateLeaderboardGuideMobileImgVariants',
	'slateLeaderboardGuideDesktopImgParentId',
	'slateLeaderboardGuideDesktopImgVariants',
] as const;

@ValidatorConstraint({ name: 'AllSlateLeaderboardGuideFieldsOrNone' })
class AllSlateLeaderboardGuideFieldsOrNoneConstraint implements ValidatorConstraintInterface {
	validate(_value: unknown, args: ValidationArguments): boolean {
		const dto = args.object as Record<string, unknown>;
		const filledCount = SLATE_LEADERBOARD_GUIDE_FIELDS.filter((field) => dto[field] != null).length;
		return filledCount === 0 || filledCount === SLATE_LEADERBOARD_GUIDE_FIELDS.length;
	}

	defaultMessage(): string {
		return 'Slate Leaderboard Guide fields must be either all filled or all null.';
	}
}

/** Fields must be either all filled (upsert) or all null (clears the record) — enforced by AllSlateLeaderboardGuideFieldsOrNoneConstraint. */
export class UpsertProbabilitySlateLeaderboardGuideDto {
	@IsUUID()
	@IsNotEmpty()
	eventId: string;

	@IsString()
	@IsOptional()
	@Validate(AllSlateLeaderboardGuideFieldsOrNoneConstraint)
	slateLeaderboardGuideTitle?: string | null;

	@IsString()
	@IsOptional()
	slateLeaderboardGuideBodyCopy?: string | null;

	@IsUUID()
	@IsOptional()
	slateLeaderboardGuideMobileImgParentId?: string | null;

	@IsOptional()
	slateLeaderboardGuideMobileImgVariants?: any | null;

	@IsUUID()
	@IsOptional()
	slateLeaderboardGuideDesktopImgParentId?: string | null;

	@IsOptional()
	slateLeaderboardGuideDesktopImgVariants?: any | null;
}

const GENERATED_SHARE_OG_TYPES = [
	ProbabilityShareType.MATCH_RESULTS,
	ProbabilityShareType.PROFILE,
	ProbabilityShareType.RANK,
] as const;

export class GetShareOgPreviewDto {
	@IsUUID()
	@IsNotEmpty()
	eventId: string;

	@IsIn([...GENERATED_SHARE_OG_TYPES])
	shareType: (typeof GENERATED_SHARE_OG_TYPES)[number];

	@IsBoolean()
	showPartnerLogo: boolean;

	@IsString()
	@IsOptional()
	partnerLogoUrl?: string | null;
}

export class GetEventFinalDataDto {
	@IsUUID()
	@IsNotEmpty()
	eventId: string;
}

export class EventSummaryStatsDto {
	forecasts: number;
	countries: number;
	universities?: number;
	bots: number;
}

export class TopGroupItemDto {
	name: string;
	score: number;
	participants: number;
}

export class LeaderboardSlateDto {
	id: string;
	label: string;
	startDate: string;
	endDate: string;
	slateTitle: string | null;
	predictionsLimit?: number;
	userPredictionsCount?: number;
}

export class GetLeaderboardSlatesResponseDto {
	slates: LeaderboardSlateDto[];
}

export class FinalStandingsGroupsDto {
	countries: TopGroupItemDto[];
	universities?: TopGroupItemDto[];
}
